import { Info } from '@phosphor-icons/react';

import { useClassHelper } from '../../../../../hooks/useClassHelper';
import { Stack } from '../../../../Stack/Stack';
import { componentName } from '../constants';
import { AddressFormValues, IValues } from '../types';
import { Button } from './shared/Button';
import { Paragraph } from './shared/Paragraph';
import { Title } from './shared/Title';

/**
 * Address-validation result screen (UES-11808). Two faces:
 * - "suggestion" (Figma 2454-871): Google returned a materially different /
 *   more complete address — entered block + suggested card, and three
 *   actions: accept the suggestion, keep the typed address as entered, or go
 *   back to the form.
 * - "unverified": Google could not confirm all components and there is no
 *   usable suggestion — entered block only, "use anyway" primary action.
 * Semantic colors are fixed (like AddressHelpBox); brand theming stays
 * confined to fonts and the primary button.
 */

const defaultTexts = {
  title: 'Suggested Address Found',
  instruction:
    'We found a more complete address that matches what you entered. Please review and confirm.',
  youEntered: 'You entered',
  suggestedAddress: 'Suggested address',
  info: 'This format helps emergency services locate you more accurately.',
  useSelected: 'Use Suggested Address',
  useAsEntered: 'Use Address as Entered',
  editAddress: 'Edit Address',
  unverifiedTitle: "We couldn't verify this address",
  unverifiedInstruction:
    "We couldn't confirm all the details of this address. You can use it as entered or edit it.",
  useAnyway: 'Use this address anyway',
} as const;

const resolveText = (
  override: string | undefined | null,
  fallback: string,
): string => {
  return override && override.trim().length > 0 ? override : fallback;
};

/* "7 - 123 Fake Street" / "Dartmouth, NS" / "B2Y 1A1" — the street line joins
 * apt-dash + civic number + street name per the design; falls back gracefully
 * when parts are missing. */
const addressLines = (address: AddressFormValues): string[] => {
  const street = [address.street_number, address.address_line_1]
    .filter(Boolean)
    .join(' ');
  const streetLine = address.address_line_2
    ? `${address.address_line_2} - ${street}`
    : street;
  const cityLine = [address.city, address.state_province_region]
    .filter(Boolean)
    .join(', ');

  return [streetLine, cityLine, address.zip_postal_code].filter(Boolean);
};

type VariantProps =
  | { variant?: 'suggestion'; suggested: AddressFormValues }
  | { variant: 'unverified'; suggested?: never };

type Props = VariantProps & {
  themeValues: IValues;
  userValues: AddressFormValues;
  isPortalPreview: boolean;
  disabled?: boolean;
  onUseSelected: () => void;
  // Suggestion face only: absent on the unverified face, whose primary action
  // already commits the typed address.
  onUseAsEntered?: () => void;
  onEdit: () => void;
  suggestions_title?: string;
  suggestion_instruction?: string;
  you_entered_label?: string;
  suggested_address_label?: string;
  suggestion_info_label?: string;
  use_selected_address?: string;
  use_address_as_entered?: string;
  suggestion_edit_address?: string;
  unverified_title?: string;
  unverified_instruction?: string;
  use_address_anyway?: string;
};

export const SuggestedAddress = ({
  variant = 'suggestion',
  themeValues,
  userValues,
  suggested,
  isPortalPreview,
  disabled = false,
  onUseSelected,
  onUseAsEntered,
  onEdit,
  suggestions_title,
  suggestion_instruction,
  you_entered_label,
  suggested_address_label,
  suggestion_info_label,
  use_selected_address,
  use_address_as_entered,
  suggestion_edit_address,
  unverified_title,
  unverified_instruction,
  use_address_anyway,
}: Props) => {
  const c = useClassHelper(componentName);

  const renderAddressBlock = (address: AddressFormValues) => {
    return addressLines(address).map(line => {
      return (
        <Paragraph key={line} fontFamily={themeValues.fontParagraph}>
          {line}
        </Paragraph>
      );
    });
  };

  const handleUseSelected = () => {
    if (isPortalPreview) {
      return;
    }
    onUseSelected();
  };

  const handleUseAsEntered = () => {
    if (isPortalPreview || !onUseAsEntered) {
      return;
    }
    onUseAsEntered();
  };

  const handleEdit = () => {
    if (isPortalPreview) {
      return;
    }
    onEdit();
  };

  const isUnverified = variant === 'unverified';

  const titleText = isUnverified
    ? resolveText(unverified_title, defaultTexts.unverifiedTitle)
    : resolveText(suggestions_title, defaultTexts.title);
  const instructionText = isUnverified
    ? resolveText(unverified_instruction, defaultTexts.unverifiedInstruction)
    : resolveText(suggestion_instruction, defaultTexts.instruction);
  const primaryLabel = isUnverified
    ? resolveText(use_address_anyway, defaultTexts.useAnyway)
    : resolveText(use_selected_address, defaultTexts.useSelected);
  const primaryTestId = isUnverified
    ? 'vowifi-suggestion-use-anyway'
    : 'vowifi-suggestion-use-selected';

  return (
    <div className={c('__suggestion')} data-testid="vowifi-suggestion">
      <Stack gap={6} direction="column" justify="start">
        <Title fontFamily={themeValues.fontTitle}>{titleText}</Title>

        <Paragraph fontFamily={themeValues.fontParagraph}>
          {instructionText}
        </Paragraph>

        <Stack gap={2} direction="column">
          <Paragraph
            fontFamily={themeValues.fontParagraph}
            fontWeight="bold"
            className={c('__suggestion-label')}
          >
            {resolveText(you_entered_label, defaultTexts.youEntered)}
          </Paragraph>
          <div
            className={c('__suggestion-entered')}
            data-testid="vowifi-suggestion-entered"
          >
            {renderAddressBlock(userValues)}
          </div>
        </Stack>

        {!isUnverified && suggested && (
          <Stack gap={2} direction="column">
            <Paragraph
              fontFamily={themeValues.fontParagraph}
              fontWeight="bold"
              className={c('__suggestion-label')}
            >
              {resolveText(
                suggested_address_label,
                defaultTexts.suggestedAddress,
              )}
            </Paragraph>
            <div
              className={c('__suggestion-card')}
              data-testid="vowifi-suggestion-card"
            >
              {renderAddressBlock(suggested)}
              <div className={c('__suggestion-info')}>
                <Info size={20} aria-hidden="true" />
                <Paragraph
                  fontFamily={themeValues.fontParagraph}
                  className={c('__suggestion-info-text')}
                >
                  {resolveText(suggestion_info_label, defaultTexts.info)}
                </Paragraph>
              </div>
            </div>
          </Stack>
        )}

        <Stack gap={3} direction="column">
          <Button
            type="button"
            onClick={handleUseSelected}
            disabled={disabled}
            aria-disabled={disabled}
            data-testid={primaryTestId}
          >
            {primaryLabel}
          </Button>
          {!isUnverified && onUseAsEntered && (
            <Button
              type="button"
              onClick={handleUseAsEntered}
              disabled={disabled}
              aria-disabled={disabled}
              ghost
              data-testid="vowifi-suggestion-use-as-entered"
            >
              {resolveText(use_address_as_entered, defaultTexts.useAsEntered)}
            </Button>
          )}
          <Button
            type="button"
            onClick={handleEdit}
            disabled={disabled}
            aria-disabled={disabled}
            ghost
            data-testid="vowifi-suggestion-edit-address"
          >
            {resolveText(suggestion_edit_address, defaultTexts.editAddress)}
          </Button>
        </Stack>
      </Stack>
    </div>
  );
};
